import { Field } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import SectionMessage from '@atlaskit/section-message';

import React, { useEffect, useState } from 'react';
import { Action, redirect } from 'redux-first-router';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Dimensions } from 'components/visuals';
import { State } from 'model/State';
import t from 'modules/i18n/intl';
import { messages } from 'modules/mac/messages';
import { fetchGetCustomerByEmail } from 'modules/mac/duck/actions/contact';
import { licensesPreselected } from 'modules/mac/duck/actions';
import {
  getCurrentMACContactEmail,
  selectGetCustomerByEmail,
  selectMakeContactPrimaryProgress,
  selectMakeContactPrimaryResult,
  selectNewContact,
  selectValidateContactResult,
  selectNewContactProgress,
} from 'modules/mac/duck/selectors';
import {
  addContactAsMain,
  addNewContactToAccount,
  fetchDetails,
  promotionNotificationEmail,
  validateContact,
} from 'modules/mac/duck/actions/accounts';
import { MessageDescriptor } from 'util/lib/types';
import { deleteFromHamsClient } from 'util/mac/api/account/delete';
import { AddContactModal } from '../AddContactModal';
import { ContactsActions } from '../ContactsActions';
import { MappedContact, ContactTypes } from '../licensesExpandedRow';
import { MakePrimaryModal } from '../MakePrimary/MakePrimaryModal';
import {
  AddContactWrapper,
  FieldWrapper,
  StyledContactsHeading,
  StyledDivWithBorder,
  StyledLoadingButton,
} from '../StyledComponents';
import { TechnicalTooltip } from '../Tooltips/Technical';
import validateEmailAddress from 'util/email';
import { checkFeatureGate, FeatureFlags } from 'util/statsig';

type PropTypes = {
  technicalContacts: Array<MappedContact>;
  billingContacts: Array<MappedContact>;
  accountId: string;
  isOpen: boolean;
  isCloudAndNotBB: boolean;
  openModal: () => any;
  closeModal: () => any;
};

export type ContactTypeNames = 'technical' | 'billing';
type PromotionError = 'irreconcilable' | 'failed' | null;

const ContactsSection: React.FC<PropTypes> = ({
  technicalContacts,
  billingContacts,
  accountId,
  isOpen,
  isCloudAndNotBB,
  openModal,
  closeModal,
}) => {
  const MACCloudReadModeFG = checkFeatureGate(FeatureFlags.CART_MAC_CLOUD_READ_MODE);

  const dispatch = useDispatch();

  const userEmail = useSelector(getCurrentMACContactEmail) ?? '';
  const customersByEmail = useSelector(selectGetCustomerByEmail);
  const state = useSelector<State, State>((s) => s);
  const validateContactResult = useSelector(selectValidateContactResult);
  const makeContactPrimaryResult = useSelector(selectMakeContactPrimaryResult);
  const makeContactPrimaryProgress = useSelector(selectMakeContactPrimaryProgress);
  const addNewContactProgress = useSelector(selectNewContactProgress);

  const [promotionModalOpen, setPromotionModalOpen] = useState<ContactTypeNames>();
  const [newBillingContact, setNewBillingContact] = useState('');
  const [newTechnicalContact, setNewTechnicalContact] = useState('');
  const [isTechnicalContactInvalid, setIsTechnicalContactInvalid] = useState(false);
  const [isBillingContactInvalid, setIsBillingContactInvalid] = useState(false);
  const [newContactType, setNewContactType] = useState('');
  const [newContact, setNewContact] = useState('');
  const [showTechnicalContactsLimitError, setShowTechnicalContactsLimitError] = useState(false);
  const [showBillingContactsLimitError, setShowBillingContactsLimitError] = useState(false);
  const [deletionErrorMessage, setDeletionErrorMessage] = useState<MessageDescriptor>();
  const [deletionError, setDeletionError] = useState();
  const newContactResponse = useSelector(selectNewContact);
  const [currentContactEmail, setCurrentContactEmail] = useState('');
  const [currentContactId, setCurrentContactId] = useState('');
  const [currentContactSmartDomain, setCurrentContactSmartDomain] = useState('');
  const [promotionError, setPromotionError] = useState<PromotionError>(null);
  const [isSubmitted, setIsSubmitted] = useState(0);

  const currentMainContactEmails = {
    technical: technicalContacts.find(({ main }) => main)?.email,
    billing: billingContacts.find(({ main }) => main)?.email,
  };
  const openPromotionModal = (kind: ContactTypeNames) => setPromotionModalOpen(kind);
  const closePromotionModal = () => setPromotionModalOpen(undefined);

  const deleteContact = (id: string, canBeDeleted: boolean) => () => {
    if (!canBeDeleted) setDeletionErrorMessage(t('mac.licenses.unable.to.delete'));
    else {
      deleteFromHamsClient(`/contactRelationship/${id}`, state)
        .then(() => {
          dispatch(fetchDetails(accountId));
        })
        .catch((error) => {
          setDeletionError(error);
          setDeletionErrorMessage(messages.errorOnDelete);
        });
    }
  };

  useEffect(() => {
    if (newContactResponse) {
      dispatch(fetchDetails(accountId));
    }
  }, [newContactResponse]);

  useEffect(() => {
    if (
      customersByEmail[newContact] &&
      !showTechnicalContactsLimitError &&
      !showBillingContactsLimitError
    ) {
      const result = customersByEmail[newContact];
      const contactCandidate = result?.result || result?._result;
      const contactsToCheck =
        newContactType === ContactTypes.TECHNICAL ? technicalContacts : billingContacts;

      if (
        contactCandidate &&
        !contactsToCheck.some(({ email }) => contactCandidate?.contactDetails?.email === email)
      ) {
        dispatch(
          addNewContactToAccount(accountId, contactCandidate.contactDetails?.id, newContactType),
        );
      } else if (result?._result === '') {
        openModal();
      }
    }
  }, [
    customersByEmail[newContact],
    showTechnicalContactsLimitError,
    showBillingContactsLimitError,
  ]);

  useEffect(() => {
    if (validateContactResult?.irreconcilable.length) {
      setPromotionError('irreconcilable');
    } else {
      dispatch(addContactAsMain(accountId, currentContactId, promotionModalOpen as string));
      const selfPromotion = userEmail === currentContactEmail;
      if (!selfPromotion) {
        dispatch(
          promotionNotificationEmail(accountId, currentContactId, promotionModalOpen as string),
        );
      }
      setIsSubmitted(isSubmitted + 1);
    }
  }, [validateContactResult]);

  useEffect(() => {
    if (isSubmitted && makeContactPrimaryResult === '') {
      setPromotionModalOpen(undefined);
      dispatch(fetchDetails(accountId));
      dispatch(
        redirect(licensesPreselected.licensePreselectedRoute(accountId.toString(), {}) as Action),
      );
    }
  }, [makeContactPrimaryResult]);

  useEffect(() => {
    if (makeContactPrimaryProgress?.failed) {
      setPromotionError('failed');
    }
  }, [makeContactPrimaryProgress]);

  const handleAddTechnicalContactClick = () => {
    setNewContact(newTechnicalContact);
    if (!newTechnicalContact || !validateEmailAddress(newTechnicalContact)) {
      setIsTechnicalContactInvalid(true);
    } else if (technicalContacts.length >= 20) {
      setShowTechnicalContactsLimitError(true);
      setIsTechnicalContactInvalid(true);
    } else {
      dispatch(fetchGetCustomerByEmail(newTechnicalContact));
    }
    setNewTechnicalContact('');
  };

  const handleAddBillingContactClick = () => {
    setNewContact(newBillingContact);
    if (!newBillingContact || !validateEmailAddress(newBillingContact)) {
      setIsBillingContactInvalid(true);
    } else if (billingContacts.length >= 20) {
      setShowBillingContactsLimitError(true);
      setIsBillingContactInvalid(true);
    } else {
      dispatch(fetchGetCustomerByEmail(newBillingContact));
    }
    setNewBillingContact('');
  };

  const handlePromotion = () => {
    if (!currentContactEmail || !currentContactId || !promotionModalOpen) return;

    const contacts = promotionModalOpen === 'technical' ? technicalContacts : billingContacts;

    const promoteeIsContact = contacts.find((c) => c.email === currentContactEmail);
    if (!promoteeIsContact) return;

    const promoterIsContact = contacts.find((c) => c.email === userEmail);
    if (!promoterIsContact) return;

    const primaryAccount = contacts.find((c) => c.main);

    const primaryEmailOrDomain =
      currentContactEmail === userEmail ||
      (currentContactEmail !== primaryAccount?.email &&
        currentContactSmartDomain === primaryAccount?.smartDomain);

    if (!primaryEmailOrDomain) return;

    dispatch(validateContact(accountId, currentContactId, promotionModalOpen));
    setPromotionError(null);
  };

  return (
    <>
      <h3>
        <FormattedMessage {...messages.contactDetails} />
      </h3>
      <SectionMessage appearance="information">
        <FormattedMessage {...messages.technicalAddressOnInvoice} />
      </SectionMessage>
      {deletionErrorMessage && (
        <SectionMessage appearance="error">
          <FormattedMessage
            {...deletionErrorMessage}
            values={{ error: (deletionError as unknown as Error)?.message }}
          />
        </SectionMessage>
      )}
      <StyledDivWithBorder>
        <StyledContactsHeading>
          <FormattedMessage {...messages.technicalContacts} />
          &nbsp;
          <TechnicalTooltip />
        </StyledContactsHeading>
      </StyledDivWithBorder>
      {technicalContacts?.map(
        ({
          id,
          name,
          main,
          email,
          canBePromoted,
          inactiveMakePrimary,
          smartDomain,
          noMakePrimary,
          canBeDeleted,
          contactId,
        }) => (
          <ContactsActions
            key={id}
            deleteContact={deleteContact}
            name={name}
            email={email}
            main={main}
            contactId={contactId}
            noMakePrimary={noMakePrimary}
            canBePromoted={canBePromoted}
            inactiveMakePrimary={inactiveMakePrimary}
            canBeDeleted={canBeDeleted}
            openPromotionModal={openPromotionModal}
            setCurrentContactEmail={() => setCurrentContactEmail(email)}
            setCurrentContactId={setCurrentContactId}
            setCurrentContactSmartDomain={setCurrentContactSmartDomain}
            smartDomain={smartDomain}
            id={id}
            isCloudAndNotBB={isCloudAndNotBB}
            MACCloudReadModeFG={MACCloudReadModeFG}
            kind="technical"
          />
        ),
      )}
      {!(isCloudAndNotBB && MACCloudReadModeFG) && (
        <AddContactWrapper>
          <FieldWrapper width={`${Dimensions.units(6.25)}%`}>
            <Field name="technical-contact" isRequired defaultValue="">
              {({ fieldProps }: any) => (
                <Textfield
                  {...fieldProps}
                  type="text"
                  data-testid="add-technical-contact"
                  placeholder="contact@example.com"
                  value={newTechnicalContact}
                  onChange={(e) => {
                    setShowTechnicalContactsLimitError(false);
                    setIsTechnicalContactInvalid(false);
                    setNewTechnicalContact(e.target.value);
                  }}
                  isInvalid={isTechnicalContactInvalid}
                  data-cy="technical-contact-input"
                />
              )}
            </Field>
          </FieldWrapper>
          <StyledLoadingButton
            isLoading={
              newContactType === ContactTypes.TECHNICAL && addNewContactProgress?.inProgress
            }
            onClick={() => {
              setNewContactType(ContactTypes.TECHNICAL);
              handleAddTechnicalContactClick();
            }}
            appearance="primary"
            isDisabled={isTechnicalContactInvalid}
            data-cy="add-technical-contact-btn"
          >
            <FormattedMessage {...messages.addBtn} />
          </StyledLoadingButton>
        </AddContactWrapper>
      )}
      {showTechnicalContactsLimitError && (
        <SectionMessage appearance="error">
          <FormattedMessage {...messages.contactsLimitReached} />
        </SectionMessage>
      )}
      <StyledDivWithBorder>
        <StyledContactsHeading>
          <FormattedMessage {...messages.billingContacts} />
          &nbsp;
          <TechnicalTooltip />
        </StyledContactsHeading>
      </StyledDivWithBorder>
      <div data-cy="billing-contacts-actions-row">
        {billingContacts?.map(
          ({
            id,
            name,
            main,
            email,
            noMakePrimary,
            inactiveMakePrimary,
            canBePromoted,
            smartDomain,
            canBeDeleted,
            contactId,
          }) => (
            <ContactsActions
              key={id}
              deleteContact={deleteContact}
              name={name}
              contactId={contactId}
              email={email}
              main={main}
              noMakePrimary={noMakePrimary}
              canBePromoted={canBePromoted}
              inactiveMakePrimary={inactiveMakePrimary}
              canBeDeleted={canBeDeleted}
              openPromotionModal={openPromotionModal}
              setCurrentContactEmail={() => setCurrentContactEmail(email)}
              setCurrentContactId={setCurrentContactId}
              setCurrentContactSmartDomain={setCurrentContactSmartDomain}
              smartDomain={smartDomain}
              id={id}
              isCloudAndNotBB={isCloudAndNotBB}
              MACCloudReadModeFG={MACCloudReadModeFG}
              kind="billing"
            />
          ),
        )}
        {!(isCloudAndNotBB && MACCloudReadModeFG) && (
          <AddContactWrapper>
            <FieldWrapper width={`${Dimensions.units(6.25)}%`}>
              <Field name="billing-contact" isRequired defaultValue="">
                {({ fieldProps }: any) => (
                  <Textfield
                    {...fieldProps}
                    type="text"
                    data-testid="add-billing-contact"
                    placeholder="contact@example.com"
                    value={newBillingContact}
                    onChange={(e) => {
                      setShowBillingContactsLimitError(false);
                      setIsBillingContactInvalid(false);
                      setNewBillingContact(e.target.value);
                    }}
                    isInvalid={isBillingContactInvalid}
                  />
                )}
              </Field>
            </FieldWrapper>
            <StyledLoadingButton
              isLoading={
                newContactType === ContactTypes.BILLING && addNewContactProgress?.inProgress
              }
              onClick={() => {
                setNewContactType(ContactTypes.BILLING);
                handleAddBillingContactClick();
              }}
              appearance="primary"
              isDisabled={isBillingContactInvalid}
              data-cy="add-billing-contact-btn"
            >
              <FormattedMessage {...messages.addBtn} />
            </StyledLoadingButton>
          </AddContactWrapper>
        )}
        {showBillingContactsLimitError && (
          <SectionMessage appearance="error">
            <FormattedMessage {...messages.contactsLimitReached} />
          </SectionMessage>
        )}
      </div>

      <AddContactModal
        isOpen={isOpen}
        closeModal={closeModal}
        newContact={newContact}
        accountId={accountId}
        newContactType={newContactType}
      />
      {promotionModalOpen && (
        <MakePrimaryModal
          closeModal={closePromotionModal}
          accountId={accountId}
          email={currentContactEmail}
          mainEmail={currentMainContactEmails[promotionModalOpen]}
          contactRelationshipType={promotionModalOpen}
          handlePromotion={handlePromotion}
          promotionError={promotionError}
        />
      )}
    </>
  );
};
export default ContactsSection;
